import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { supabase } from '../config/supabaseClient';
import { GROUP_TYPES, GROUP_VISIBILITY, SKILL_LEVELS } from '../constants';

export default function GroupDetailsModal({ show, onClose, group }) {
  const [adminProfile, setAdminProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show && group?.admin_id) {
      fetchAdminProfile();
    }
  }, [show, group]);

  const fetchAdminProfile = async () => {
    if (!group?.admin_id) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name, skill_level')
        .eq('id', group.admin_id)
        .single();

      if (error) {
        console.error('Error fetching admin profile:', error);
      } else {
        setAdminProfile(data);
      }
    } catch (err) {
      console.error('Error fetching admin profile:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!show || !group) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getSkillLevelLabel = (skillLevel) => {
    return Object.entries(SKILL_LEVELS).find(([key, value]) => value === skillLevel)?.[0] || skillLevel;
  };

  const getGenderLabel = (gender) => {
    return Object.entries(GROUP_TYPES).find(([key, value]) => value === gender)?.[0] || gender;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <h2 className="text-xl font-bold text-textDark">{group.name}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Group Info */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-textDark">Group Information</h3>
            
            {/* Description */}
            {group.description && (
              <div>
                <label className="block text-sm font-medium text-textMed mb-2">Description</label>
                <p className="text-textDark bg-gray-50 p-3 rounded-lg text-sm leading-relaxed">
                  {group.description}
                </p>
              </div>
            )}

            {/* Member Count */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-textMed">Members</span>
              <span className="text-textDark font-semibold">{group.memberCount || 0}</span>
            </div>

            {/* Created Date */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-textMed">Created</span>
              <span className="text-textDark">{formatDate(group.created_at)}</span>
            </div>
          </div>

          {/* Group Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-textDark">Group Settings</h3>
            
            {/* Visibility */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-textMed">Visibility</span>
              <span className={`px-3 py-1 rounded-full text-sm ${
                group.visibility === GROUP_VISIBILITY.PUBLIC
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {group.visibility.charAt(0).toUpperCase() + group.visibility.slice(1)}
              </span>
            </div>

            {/* Gender */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-textMed">Gender</span>
              <span className="px-3 py-1 bg-primary/5 text-primary rounded-full text-sm">
                {getGenderLabel(group.gender)}
              </span>
            </div>

            {/* Skill Level */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-textMed">Skill Level</span>
              <span className="px-3 py-1 bg-primary/5 text-primary rounded-full text-sm">
                NTRP {group.skill_level}
              </span>
            </div>
          </div>

          {/* Group Creator */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-textDark">Group Creator</h3>
            
            {isLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : adminProfile ? (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-primary font-semibold text-lg">
                      {adminProfile.first_name?.[0] || 'U'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-textDark">
                      {adminProfile.first_name} {adminProfile.last_name}
                    </p>
                    {adminProfile.skill_level && (
                      <p className="text-sm text-textMed">
                        NTRP {adminProfile.skill_level}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4 text-textMed">
                <p>Creator information not available</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full py-3 px-4 bg-gray-100 text-textDark rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

GroupDetailsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  group: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    visibility: PropTypes.string.isRequired,
    gender: PropTypes.string.isRequired,
    skill_level: PropTypes.string.isRequired,
    admin_id: PropTypes.string.isRequired,
    created_at: PropTypes.string.isRequired,
    memberCount: PropTypes.number
  })
};
